'use client';

import type { FC } from 'react';
import { CONFIG } from '@frameio/config';
import type { LocaleId } from '@frameio/i18n';
import { DEFAULT_LOCALE } from '@frameio/i18n';
import { LocaleProvider as LocaleProviderImplementation } from '@frameio/i18n/client';
import { useUxpHostInfoContext } from '@frameio/integrations/shared/contexts/UxpHostInfoContext';
import { useGetDvaDebugFlagsQuery } from '@frameio/shared/integrations/gql/queries/generated/GetDvaDebugFlags';
import { isUXPEnvironment } from '@frameio/utils/config';

export type LocaleProviderProps = {
  children: React.ReactNode;
};

export const LocaleProvider: FC<LocaleProviderProps> = CONFIG.enableI18n
  ? (props) => {
      const { uiLocale } = useUxpHostInfoContext();
      const dvaDebugInfo = useGetDvaDebugFlagsQuery({
        context: { resolveWithUXP: true },
        skip: !isUXPEnvironment(),
      })?.data?.getDvaDebugFlags;
      const dvaEnvironment = dvaDebugInfo?.environment || 'prod'; // local, dev, staging, prod
      const locale = (uiLocale || DEFAULT_LOCALE) as LocaleId;
      // enable all DVA9 locales in non-prod DVA environments, regardless of the uiLocale provided by the UXP host, to facilitate i18n testing
      const isI18nEnabled =
        process.env.NODE_ENV !== 'production' ||
        dvaEnvironment !== 'prod' ||
        ['en-US', 'ja-JP', 'zz-ZZ'].includes(locale);
      if (!isI18nEnabled) {
        return props.children;
      }

      return <LocaleProviderImplementation {...props} locale={locale} />;
    }
  : ({ children }) => children;
